/** Phase F (tainan package): Tainan, Taiwan's oldest city. Lines marked “English practice” stay in English in every locale. */
export const enTainan = {
  tainan: {
    mapNames: { tainan: 'Tainan' },
    zones: {
      tn_city: 'Tainan old town',
      tn_station: 'HSR Tainan station',
      tn_chihkan: 'Chihkan Tower',
      tn_shennong: 'Shennong Street',
      tn_anping: 'Anping',
      tn_market: 'Guohua Street market',
      tn_confucius: 'Confucius Temple',
      tn_canal: 'Anping canal',
    },
    objects: {
      tn_hsr: 'HSR station · Tainan',
      tn_shuttle: 'Shuttle bus stop (H31)',
      tn_chihkan: 'Chihkan Tower',
      tn_turtles: 'Stone turtles',
      tn_lanterns: 'Lantern workshop',
      tn_zeelandia: 'Fort Zeelandia',
      tn_treehouse: 'Anping Tree House',
      tn_shrimp: 'Shrimp-cracker stall',
      tn_candied: 'Candied-fruit shop',
      tn_boatRide: 'Canal boat tour',
      tn_beefSoup: 'Beef-soup stall',
      tn_danzai: 'Danzai noodle stall',
      tn_coffin: 'Coffin-bread stall',
      tn_confucius: 'Confucius Temple hall',
    },
    npc: {
      tn_soupGranny: 'Beef-soup granny',
      tn_fortGuide: 'Fort guide',
      tn_scholar: 'Confucian student',
      tn_crackerLady: 'Shrimp-cracker seller',
      tn_candiedAunt: 'Candied-fruit auntie',
      tn_fisher: 'Canal fisherman',
      tn_kid1: 'Boy with a kite',
      tn_kid2: 'Girl with a sketchbook',
      tn_lanternArtist: 'Lantern artist',
      tn_shuttleDriver: 'Shuttle driver',
      tn_cyclist: 'Cyclist',
      tn_grandpa: 'Chess grandpa',
      tn_boatman: 'Boatman',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    shuttle: {
      text: 'HSR Tainan station is out in Guiren, 10 km from the old town. The free H31 shuttle bus runs to the city centre every 20 minutes — about half an hour’s ride.',
      ask: '🚌 Ask the driver (English)',
      q: 'How do you ask if this bus goes downtown?',
      // English practice
      a: ['Excuse me, does this bus go to the city centre?', 'Bus! City! Now!', 'You drive me downtown.'],
      right: '“Yes! Chihkan Tower is the fourth stop. Hold on tight!”',
      wrong: 'The driver raises an eyebrow. Try a politer question — start with “Excuse me…”',
    },
    chihkan: {
      text: 'Chihkan Tower (赤崁樓) stands on Fort Provintia, built by the Dutch in 1653. The Qing dynasty later added these two pavilions: the Sea God Temple and the Wenchang Pavilion for students.',
      learn: '🏯 Who built the fort? (English)',
      q: 'Who built the first fort here?',
      // English practice
      a: ['The Dutch, in 1653.', 'The Romans, in 100.', 'The Japanese, in 1950.'],
      right: 'Right! The Dutch ruled Tainan for 38 years, until Koxinga (鄭成功) drove them out in 1662.',
      wrong: 'Look at the brick base — it is 370 years old…',
    },
    turtles: {
      text: 'Nine stone turtles (贔屭) carry tall stelae on their backs — a gift from the Qianlong emperor in 1788. Legend says one turtle slipped into the harbour and had to be replaced!',
      count: '🐢 Count the turtles (English)',
      q: 'How many turtles were there at first?',
      // English practice: number words
      a: ['Nine.', 'Seven.', 'Twelve.'],
      right: 'Nine! A turtle with a stele on its back means a long life and good news.',
      wrong: 'Count again — on your fingers, in English…',
    },
    confucius: {
      text: 'The Confucius Temple (孔廟) was founded in 1665 — the first in Taiwan. Over the gate hangs the plaque 全臺首學: “the first school of all Taiwan”. Students still come here before exams.',
      learn: '📜 Read the plaque (English)',
      q: 'What does 全臺首學 mean?',
      // English practice
      a: ['The first school of all Taiwan.', 'The biggest market of Taiwan.', 'No running in the temple.'],
      right: 'Yes! “First” (首) and “school” (學). You bow once to the master, like the students do.',
      wrong: 'Hint: this was a school, not a market…',
    },
    zeelandia: {
      text: 'Fort Zeelandia (安平古堡) was the Dutch headquarters in Taiwan, built in 1624. In 1662 Koxinga besieged it for nine months — then the Dutch sailed away. Only a few red-brick walls are original; the white tower is from 1930.',
      learn: '🏰 Which country? (English)',
      q: 'Which country built Fort Zeelandia?',
      // English practice
      a: ['The Netherlands.', 'Spain.', 'Australia.'],
      right: 'Correct — the Dutch! Their bricks came all the way from Java as ship ballast.',
      wrong: 'The flag on the tower has red, white and blue stripes…',
    },
    treehouse: {
      text: 'The Anping Tree House (安平樹屋) was a warehouse of the British firm Tait & Co. After it was abandoned, a banyan grew over the walls, through the windows and across the roof — now the tree IS the house.',
      learn: '🌳 Describe it (English)',
      q: 'What happened to the warehouse?',
      // English practice: past tense
      a: ['A tree grew over it.', 'A tree will grow over it.', 'A tree is eating my lunch.'],
      right: '“Grew” — the past of “grow”. The roots look like curtains of rope!',
      wrong: 'It already happened, long ago. Which answer is in the past?',
    },
    lanterns: {
      text: 'Shennong Street’s lantern maker paints paper lanterns by hand — red for luck, yellow for wealth, and one with a dragon for the Lantern Festival.',
      paint: '🏮 Pick a colour (English)',
      q: 'The artist holds up a lantern the colour of a ripe tomato. What colour is it?',
      // English practice: colours
      a: ['Red.', 'Blue.', 'Green.'],
      right: 'Red! She writes 福 (good fortune) on it and hangs it over the lane for you.',
      wrong: 'Look at the tomatoes on the table for a hint…',
      closed: 'The shutters are down; the lantern maker starts work after three in the afternoon.',
    },
    boat: {
      text: 'A tour boat glides along the Anping canal, under the bridges and out towards the old harbour. The skipper tells stories of the salt boats that used this water a hundred years ago.',
      ride: '🚤 Buy a ticket (English)',
      price: 50,
      poor: 'A boat ticket costs 50 coins. Come back with more pocket money!',
      q: 'How do you ask for a ticket?',
      // English practice
      a: ['One ticket for the boat tour, please.', 'Boat. Me. Go.', 'Give ticket boat now.'],
      right: 'Thirty minutes on the water: egrets on the banks, the fort tower in the distance, a cool breeze. Lovely!',
      wrong: 'The boatman smiles patiently. Try “…please”.',
      closed: 'The last boat has left for today. Tours run from nine to six.',
    },
    beefSoup: {
      vendor: 'Fresh beef soup! The beef is sliced thin and cooked in the hot broth right in your bowl. One bowl?',
      ok: 'One bowl of beef soup with rice, please.',
      bad: ['Beef. Bowl. Hurry.', 'I want beef soup, but no beef and no soup.'],
      done: 'The beef is pink and tender, the broth sweet with ginger. Granny adds a free spoon of rice. “Eat slowly!”',
      price: 45,
    },
    danzai: {
      vendor: 'Danzai noodles — the Tainan way since 1895! A small bowl with a shrimp on top. Would you like one?',
      ok: 'Yes, please. One bowl of danzai noodles.',
      bad: ['Noodle give.', 'I eat all noodles, you pay.'],
      done: 'A little bowl: noodles, minced pork, a whole shrimp and a drop of black vinegar. Small, but perfect.',
      price: 40,
    },
    coffin: {
      vendor: 'Coffin bread! Don’t worry — it’s just a box of fried toast filled with creamy chicken stew. Hungry?',
      ok: 'Could I have one coffin bread, please?',
      bad: ['Coffin! Scary!', 'Toast box me want.'],
      done: 'Crunchy outside, creamy inside. The name comes from the shape — a toast box with a lid. Yum!',
      price: 35,
    },
    shrimp: {
      vendor: 'Anping shrimp crackers, fried this morning! Light as air. Original or spicy?',
      ok: 'One bag of the original crackers, please.',
      bad: ['Crackers. All. Mine.', 'Spicy no original yes give.'],
      done: 'A huge pink bag, crispy and salty-sweet. The seller tucks in an extra one “for the road”.',
      price: 30,
    },
    candied: {
      vendor: 'Candied fruit from Anping: plums, mango, olives, tomatoes with ginger… Try a sample?',
      ok: 'Could I try the candied mango, please?',
      bad: ['Candy sample free all.', 'Me eat jar.'],
      done: 'Sweet, sour and a little salty. The auntie wraps a little bag of mango and a few plums for the train.',
      price: 35,
    },
    closedMorning: 'The pot is empty! Beef soup is a breakfast dish — come before two in the afternoon.',

    talk: {
      tn_soupGranny: [
        'I have sold beef soup here for forty years. The beef comes from Shanhua, fresh every dawn.',
        'English practice: “Would you like rice with that?” — “Yes, please!”',
        'Tainan people eat sweet. Even our soup is a little sweet!',
      ],
      tn_fortGuide: [
        'These red bricks came from Java as ballast in Dutch ships. Four hundred years old!',
        'English practice: “Where are you from?” — “I’m from Taiwan.”',
        'From the tower you can see the sea — in 1624 the water came right up to this wall.',
      ],
      tn_scholar: [
        'Students leave their exam tickets on the altar for good luck. I left mine last week.',
        'English practice: “Good luck on your exam!” — “Thank you!”',
        'Confucius said: learning without thinking is useless. Thinking without learning is dangerous.',
      ],
      tn_crackerLady: [
        'Shrimp crackers are made from real shrimp and tapioca. Watch them puff up in the oil!',
        'English practice: “How much is one bag?” — “Thirty dollars.”',
      ],
      tn_candiedAunt: [
        'We dry the fruit in the sun first, then soak it in sugar and salt. Three weeks for a good plum!',
        'English practice: “Can I try one?” — “Of course, here you are.”',
      ],
      tn_fisher: [
        'The canal was dug in 1926 to carry fish and salt to the city. Now it carries tourists!',
        'English practice: “Did you catch anything?” — “Not yet… the fish are sleeping.”',
        'Watch the egrets. They know where the fish are.',
      ],
      tn_kid1: [
        'My kite is a swallow! The wind on the canal is the best in Tainan.',
        'English practice: “Look! It’s flying!” — “Wow!”',
      ],
      tn_kid2: [
        'I’m drawing the turtles. Nine turtles — one, two, three… the fourth one has a chipped nose.',
        'English practice: “What are you drawing?” — “I’m drawing a turtle.”',
      ],
      tn_lanternArtist: [
        'Every lantern on this street is painted by hand. The dragon one took me three days.',
        'English practice: “What colour do you like?” — “I like red.”',
        'At night Shennong Street turns gold. Stay till the lanterns come on!',
      ],
      tn_shuttleDriver: [
        'H31 to the city centre, free with your HSR ticket. Next bus in ten minutes.',
        'English practice: “Does this bus stop at Chihkan Tower?” — “Yes, the fourth stop.”',
      ],
      tn_cyclist: [
        'Tainan is flat — perfect for cycling. The canal path goes all the way to the sea.',
        'English practice: “Turn left at the bridge.”',
      ],
      tn_grandpa: [
        'Chinese chess. The general cannot leave his palace — like me, I never leave Tainan!',
        'English practice: “Your move.” — “Hmm… let me think.”',
        'When I was a boy, the canal still had sailing boats carrying salt.',
      ],
      tn_boatman: [
        'The tour goes under five bridges. Duck your head at the third one!',
        'English practice: “Please sit down and hold the rail.”',
      ],
    },
  },
};
